import { assocPath, clone } from 'ramda';
import { createContext, type ReactNode, useContext, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClipBoard } from '../../../../../ClipBoardProvider';
import translations from '../../../../../translations';
import type {
    PageContainer,
    PageContainerRequestAPI,
    PageElementRequestAPI,
} from '../../../../../types/page';
import type { ComposerElement } from '../../../../../types/pageEditor';
import type { TabType } from '../../../../../types/tab';
import { generateRouteUrl } from '../../../../../utils/routing';
import { useNotificationDispatch } from '../../../../notifications/NotificationProvider';
import { useTabsDispatch } from '../../../../tabs/TabsProvider';
import { PAGE_ELEMENT_TYPES } from '../../../constants';
import type { ContainerType } from '../../dnd-utils';
import { usePageEditor } from '../../PageEditorProvider';
import ComposerReducer, { type ComposerAction } from './ComposerReducer';

export interface ElementSelection {
    id?: string | null;
    targetId: string | null;
    order?: number;
    containerType?: string | undefined | null;
    pageElementType?: string;
    componentType?: string | undefined;
    select?: boolean;
    edit?: boolean;
    partialElement?: Partial<ComposerElement>;
    title?: string;
    message?: string;
    tabType?: TabType;
}

interface ComposerProviderContext {
    dragDropElements: ComposerElement[];
    elementToDelete: ElementSelection | null;
    setElementToDelete: (selection: ElementSelection | null) => void;
    toggleSelectedElement: (id: string) => void;
    generateDragDropElements: () => void;
    onSavePage: (updatedPage: PageElementRequestAPI) => void;
    onPageElementDrop: (args: ElementSelection) => void;
    onPageElementCopy: (selection: ElementSelection) => void;
    onPageElementPaste: (selection: ElementSelection) => void;
    onPageElementRemove: (selection: ElementSelection | null) => void;
    onSetPageElementActive: (id: string | null) => void;
    onPageContainerTypeUpdate: (containerId: string, containerType: ContainerType) => void;
    onPageElementUpdate: (id: string, path: string[], value: unknown) => void;
    elementToEdit: string | null;
    setElementToEdit: (elementId: string) => void;
    elementSelected: string | null;
}

const Context = createContext<ComposerProviderContext | undefined>(undefined);

interface ComposerProviderProps {
    tabKey: string;
    children: ReactNode;
}

const ComposerProvider = ({ tabKey, children }: ComposerProviderProps) => {
    const { copy, paste } = useClipBoard();
    const dispatchTabs = useTabsDispatch();

    const navigate = useNavigate();

    const dispatchNotification = useNotificationDispatch();

    const { state: pageBuilderState, onSave, updatePage } = usePageEditor();
    const { page } = pageBuilderState;

    const [dragDropElements, dispatch] = useReducer(ComposerReducer, [] as ComposerElement[]);

    const [elementToDelete, setElementToDelete] = useState<ElementSelection | null>(null);
    const [elementToEdit, setElementToEdit] = useState<string | null>(null);
    const [elementSelected, setElementSelected] = useState<string | null>(null);

    const update = (action: ComposerAction, hasUnsavedChanges = true) => {
        dispatch(action);

        // Update the confirmation flag
        // only when there are unsaved changes.
        if (hasUnsavedChanges) {
            dispatchTabs({
                type: 'SET_CLOSE_CONFIRMATION_REQUIRED',
                payload: {
                    tabKey,
                    isRequired: true,
                },
            });
        }
    };

    const toggleSelectedElement = (id: string) => {
        setElementSelected(id === elementSelected ? null : id);
    };

    const generateDragDropElements = () => {
        const flattendContainers = [] as ComposerElement[];

        const clonedPage = clone(page);

        const flattenPageContainers = (
            containers: PageContainerRequestAPI[],
            parentContainerId: string | null = null,
        ) => {
            containers.forEach((container) => {
                if (container.pageContainers) {
                    flattenPageContainers(container.pageContainers, container.id);
                }

                // Inclusion of nested containers
                // would cause data duplication
                container.pageContainers = null;

                const element = container as ComposerElement;

                // The parent container ID is used as a foreign key
                element.parentId = parentContainerId;
                flattendContainers.push(element);
            });
        };

        const { pageComponents, pageContainers } = clonedPage;

        flattenPageContainers(pageContainers || []);

        const formattedPageComponents = pageComponents
            ? (pageComponents.map((pageComponent) => ({
                  ...pageComponent,
                  parentId: pageComponent.pageContainerId,
              })) as ComposerElement[])
            : [];

        update(
            {
                type: 'set_composer_elements',
                payload: flattendContainers.concat(formattedPageComponents),
            },
            false,
        );
    };

    const generateNestedContainers = (
        flattenedPageContainers: ComposerElement[],
        parent: string | null | undefined,
    ) => {
        const containersNested: PageContainer[] = [];
        flattenedPageContainers.forEach((pageContainer) => {
            if (pageContainer.parentId === parent) {
                const children = generateNestedContainers(
                    flattenedPageContainers,
                    pageContainer.id || '',
                );

                const containerWithChildren: ComposerElement = assocPath(
                    ['pageContainers'],
                    children.length ? children : null,
                    pageContainer,
                );

                delete containerWithChildren.parentId;
                containersNested.push(containerWithChildren);
            }
        });

        return containersNested;
    };

    const generatePageComponents = (pageElements: ComposerElement[]) => {
        return pageElements
            .filter((element) => Object.hasOwn(element, 'componentType'))
            .map((pageComponent) => {
                const formattedPageComponent = {
                    ...pageComponent,
                    pageContainerId: pageComponent.parentId,
                    pageContainerDeveloperName: pageElements.find(
                        (pe) => pe.id === pageComponent.parentId,
                    )?.developerName,
                };
                delete formattedPageComponent.parentId;
                return formattedPageComponent;
            });
    };

    const onSavePage = async (updatedPage: PageElementRequestAPI) => {
        const pageExists = updatedPage.id;

        const root = dragDropElements.find((childContainer) => childContainer.parentId === null);
        const nestedPageContainers = generateNestedContainers(
            dragDropElements.filter((container) => Object.hasOwn(container, 'containerType')),
            root?.parentId,
        );

        const pageComponents = generatePageComponents(dragDropElements);
        updatedPage = assocPath(['pageContainers'], nestedPageContainers, updatedPage);
        updatedPage = assocPath(
            ['pageComponents'],
            pageComponents.length > 0 ? pageComponents : null,
            updatedPage,
        );

        const savedPage = await onSave(updatedPage);

        if (savedPage) {
            if (!pageExists) {
                const route = generateRouteUrl({
                    tabType: 'page',
                    options: {
                        tabKey,
                        elementId: savedPage.id || null,
                    },
                });

                dispatchTabs({
                    type: 'SET_ELEMENT_ID',
                    payload: {
                        tabKey,
                        elementId: savedPage.id,
                        tabType: 'page',
                    },
                });

                navigate(route, {
                    replace: true,
                });
            } else {
                dispatchTabs({
                    type: 'SET_CLOSE_CONFIRMATION_REQUIRED',
                    payload: {
                        tabKey,
                        isRequired: false,
                    },
                });
            }

            updatePage(savedPage as PageElementRequestAPI);
        }
    };

    const onPageElementDrop = ({
        id,
        targetId,
        order,
        containerType,
        pageElementType,
        componentType,
        select = true,
        edit = !id,
        partialElement = {} as Partial<ComposerElement>,
    }: ElementSelection) => {
        const elementId = id || crypto.randomUUID();

        if (containerType && pageElementType === PAGE_ELEMENT_TYPES['container']) {
            update({
                type: 'drop_composer_container',
                payload: {
                    id: elementId,
                    parentId: targetId,
                    order: order as number,
                    containerType,
                    partialElement,
                },
            });
        }

        if (componentType && pageElementType === PAGE_ELEMENT_TYPES['component']) {
            update({
                type: 'drop_composer_component',
                payload: {
                    id: elementId,
                    parentId: targetId,
                    order: order as number,
                    componentType,
                    partialElement,
                },
            });
        }

        if (edit) {
            setElementToEdit(elementId);
            if (select) {
                setElementSelected(elementId);
            }
        }
    };

    const onPageElementCopy = ({ targetId }: ElementSelection) => {
        const clonedElements: ComposerElement[] = [];

        const findChildElements = (parentId: string | null) => {
            const childElements = dragDropElements.filter((dde) => dde.parentId === parentId);
            if (childElements.length > 0) {
                childElements.forEach((ce) => {
                    findChildElements(ce.id);
                    clonedElements.push(ce);
                });
            }
        };

        const elementToClone = dragDropElements.find(
            (dde) => dde.id === targetId,
        ) as ComposerElement;

        findChildElements(elementToClone.id);

        clonedElements.push({
            ...elementToClone,
            parentId: null,
        });

        copy(clonedElements);

        dispatchNotification({
            type: 'notifySuccess',
            payload: { message: translations.PAGE_element_copy_success },
        });
    };

    const onPageElementPaste = ({ targetId }: ElementSelection) => {
        const elementToPasteInto = dragDropElements.find((dde) => dde.id === targetId);

        if (elementToPasteInto && Object.hasOwn(elementToPasteInto, 'containerType')) {
            try {
                const elementsToPaste = paste() as ComposerElement[];

                if (elementsToPaste.length === 0) {
                    throw new Error(translations.PAGE_element_paste_error);
                }

                const clonedElements: ComposerElement[] = [];

                const findChildElementsAndReplaceIds = (
                    parentId: string | null,
                    newParentId: string,
                ) => {
                    const childElements = elementsToPaste.filter(
                        (dde) => dde.parentId === parentId,
                    );
                    if (childElements.length > 0) {
                        childElements.forEach((ce) => {
                            const newChildId = crypto.randomUUID();
                            findChildElementsAndReplaceIds(ce.id, newChildId);
                            clonedElements.push({ ...ce, id: newChildId, parentId: newParentId });
                        });
                    }
                };

                const elementToClone = elementsToPaste.find(
                    (dde) => dde.parentId === null,
                ) as ComposerElement;

                const newId = crypto.randomUUID();

                findChildElementsAndReplaceIds(elementToClone.id, newId);

                const rootChildElements = dragDropElements.filter(
                    (dde) => dde.parentId === targetId,
                );
                clonedElements.push({
                    ...elementToClone,
                    id: newId,
                    parentId: targetId,
                    order: rootChildElements.length,
                });

                update({
                    type: 'set_composer_elements',
                    payload: dragDropElements.concat(clonedElements),
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        }
    };

    const onPageElementRemove = (selection: ElementSelection | null) => {
        update({
            type: 'remove_composer_element',
            payload: {
                id: selection?.targetId || '',
            },
        });
    };

    const onSetPageElementActive = (id: string | null) => {
        setElementToEdit(id === elementToEdit ? null : id);
    };

    const onPageContainerTypeUpdate = (containerId: string, containerType: ContainerType) => {
        update({
            type: 'set_composer_container_type',
            payload: {
                containerId,
                containerType,
            },
        });
    };

    const onPageElementUpdate = (id: string, path: string[], value: unknown) => {
        update({
            type: 'set_composer_element',
            payload: {
                id,
                path,
                value,
            },
        });
    };

    const contextValue = {
        dragDropElements,
        elementToDelete,
        setElementToDelete,
        toggleSelectedElement,
        generateDragDropElements,
        onSavePage,
        onPageElementDrop,
        onPageElementCopy,
        onPageElementPaste,
        onPageElementRemove,
        onSetPageElementActive,
        onPageContainerTypeUpdate,
        onPageElementUpdate,
        elementToEdit,
        setElementToEdit,
        elementSelected,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useComposer = (): ComposerProviderContext => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useComposer must be used within a ComposerProvider');
    }
    return context;
};

export { ComposerProvider, useComposer };
